<template>
  <section class="prk" aria-labelledby="prk-h">
    <div class="prk-top">
      <div class="prk-titles">
        <div class="prk-eyebrow">Visibility</div>
        <h2 id="prk-h">Where AI ranks {{ providerName }}</h2>
        <p>AI's top 5 providers for each question we asked. Pick a patient type.</p>
      </div>
      <div class="prk-legend">
        <span><i class="sw me" aria-hidden="true" />{{ providerName }}</span>
        <span><i class="sw org" aria-hidden="true" />Other {{ hospital || 'same hospital' }} provider</span>
        <span><i class="sw other" aria-hidden="true" />Other hospital</span>
      </div>
    </div>

    <template v-if="personas.length && current">
      <div role="tablist" aria-label="Patient type" class="prk-tabs">
        <button
          v-for="(p, i) in personas"
          :key="p.id"
          type="button"
          role="tab"
          :aria-selected="i === tab ? 'true' : 'false'"
          :class="{ on: i === tab }"
          @click="tab = i"
        >{{ p.name }} <span class="sc">&middot; {{ p.score == null ? '—' : Math.round(p.score) + '%' }}</span></button>
      </div>
      <div class="prk-note">In AI's top 5 for <strong>{{ current.in_top_5 }} of {{ current.questions.length }}</strong> questions</div>

      <div class="prk-grid">
        <div v-for="q in current.questions" :key="q.id" class="prk-card">
          <div class="prk-qtop">
            <div class="prk-qwrap">
              <span class="prk-q">&ldquo;{{ q.text }}&rdquo;</span>
            </div>
            <span class="prk-badge" :class="badgeClass(q)">
              <strong>{{ q.rank ? '#' + q.rank : 'Not in top 5' }}</strong>
              <span>{{ q.points }} pts</span>
            </span>
          </div>
          <ol aria-label="AI's top 5 providers" class="prk-rows">
            <li v-for="r in (q.top_5 || []).slice(0, 5)" :key="r.rank" class="prk-row" :class="rowClass(r)">
              <span class="num">{{ r.rank }}</span>
              <span class="who">
                <span class="nm">{{ r.name }}</span>
                <span v-if="r.hospital" class="hosp">{{ r.hospital }}</span>
              </span>
            </li>
          </ol>
          <template v-if="grounded">
            <button
              v-if="(q.sources || []).length"
              type="button"
              class="prk-src-btn"
              :aria-expanded="open[q.id] ? 'true' : 'false'"
              @click="open[q.id] = !open[q.id]"
            >
              <v-icon size="16">mdi-link-variant</v-icon>{{ open[q.id] ? 'Hide sources' : 'See sources' }}
            </button>
            <div v-if="open[q.id] && (q.sources || []).length" class="prk-src">
              <span class="cap">Pages AI used for this answer</span>
              <ul>
                <li v-for="(s, i) in q.sources" :key="i">
                  <a v-if="safeUrl(s.url)" :href="s.url" target="_blank" rel="noopener noreferrer">{{ s.site }}</a>
                  <span v-else class="plain">{{ s.site }}</span>
                  <span class="t">{{ s.title }}</span>
                  <span v-if="s.validated" title="Link checked" class="ok">&#10003;</span>
                </li>
              </ul>
            </div>
          </template>
        </div>
      </div>
    </template>
    <p v-else class="prk-note">No answers yet.</p>

    <ProviderSitesUsed :sites="sites" @go-to-tab="$emit('go-to-tab', $event)" />
    <span v-if="!grounded" class="prk-foot">Sources come from Gemini (Grounded). Other AI tools don't share their sources.</span>
  </section>
</template>

<script>
import ProviderSitesUsed from './ProviderSitesUsed.vue'

export default {
  name: 'ProviderRankings',
  components: { ProviderSitesUsed },
  props: {
    personas: { type: Array, default: () => [] },
    providerName: { type: String, default: '' },
    llmProvider: { type: String, default: 'google_grounded' },
    hospital: { type: String, default: '' },
    sites: { type: Array, default: () => [] }
  },
  emits: ['go-to-tab'],
  data() {
    return { tab: 0, open: {} }
  },
  computed: {
    grounded() {
      return this.llmProvider === 'google_grounded'
    },
    current() {
      return this.personas[this.tab] || this.personas[0] || null
    }
  },
  watch: {
    // A poll refresh keeps the tab and open panels. ProvidersTab keys this component
    // by provider and AI tool, so those reset on a switch. Only fall back if the tab is gone.
    // Follow the selected persona by id, so a refresh that reorders or replaces
    // personas never leaves the tab showing a different one.
    personas(next, prev) {
      const id = (prev || [])[this.tab]?.id
      const idx = id == null ? -1 : next.findIndex(p => p.id === id)
      this.tab = idx >= 0 ? idx : 0
    }
  },
  methods: {
    safeUrl(u) {
      return /^https?:\/\//i.test(u || '')
    },
    badgeClass(q) {
      if (!q.rank) return 'miss'
      return q.rank <= 2 ? 'hi' : 'mid'
    },
    rowClass(r) {
      if (r.is_this_provider) return 'me'
      if (r.same_organization) return 'org'
      return 'other'
    }
  }
}
</script>

<style scoped>
.prk { background: #fff; border: 1px solid #e6e1d4; border-radius: 12px; padding: 28px; display: flex; flex-direction: column; gap: 20px; }
.prk-top { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-end; gap: 16px; }
.prk-titles { display: flex; flex-direction: column; gap: 6px; max-width: 640px; }
.prk-eyebrow { font-size: 12px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--brand-ocean-600); }
.prk h2 { margin: 0; font-size: 24px; font-weight: 700; }
.prk p { margin: 0; color: #4a5568; }
.prk-legend { display: flex; gap: 16px; flex-wrap: wrap; font-size: 13px; color: #4a5568; align-items: center; }
.prk-legend span { display: flex; gap: 6px; align-items: center; }
.sw { width: 14px; height: 14px; border-radius: 4px; display: inline-block; box-sizing: border-box; }
.sw.me { background: var(--brand-ocean-600); }
.sw.org { background: var(--brand-ocean-100); }
.sw.other { background: #fff; border: 1px solid #cfc8b6; }
.prk-tabs { display: flex; gap: 8px; flex-wrap: wrap; }
.prk-tabs button { height: 44px; padding: 0 16px; border-radius: 999px; font: inherit; font-weight: 600; font-size: 14px; cursor: pointer; background: #fff; color: var(--brand-ink-950); border: 1px solid #cfc8b6; }
.prk-tabs button.on { background: var(--brand-ink-950); color: #fff; border-color: var(--brand-ink-950); }
.prk-tabs .sc { font-weight: 500; opacity: 0.85; }
.prk-note { font-size: 14px; color: #33465e; }
.prk-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 16px; }
.prk-card { border: 1px solid #e6e1d4; background: var(--brand-sand-50); border-radius: 10px; padding: 16px; display: flex; flex-direction: column; gap: 12px; }
.prk-qtop { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.prk-qwrap { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.prk-q { font-weight: 600; font-size: 14px; min-height: 42px; }
.prk-badge { flex: 0 0 auto; display: flex; flex-direction: column; align-items: flex-end; padding: 6px 12px; border-radius: 8px; text-align: right; }
.prk-badge strong { font-size: 14px; white-space: nowrap; }
.prk-badge span { font-size: 11px; }
.prk-badge.hi { background: var(--brand-ocean-600); color: #fff; }
.prk-badge.mid { background: var(--brand-ocean-100); color: var(--brand-ink-950); }
.prk-badge.miss { background: #fdf1d8; color: #6b4f00; }
.prk-rows { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
.prk-row { display: flex; gap: 10px; align-items: center; padding: 7px 10px; border-radius: 6px; font-size: 14px; min-width: 0; box-sizing: border-box; }
.prk-row.me { background: var(--brand-ocean-600); color: #fff; font-weight: 700; }
.prk-row.org { background: var(--brand-ocean-100); color: var(--brand-ink-950); }
.prk-row.other { background: #fff; color: var(--brand-ink-950); border: 1px solid #e6e1d4; }
.num { flex: 0 0 auto; width: 22px; height: 22px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; background: var(--brand-ink-950); color: #fff; }
.prk-row.me .num { background: #fff; color: var(--brand-ocean-600); }
.who { display: flex; flex-direction: column; min-width: 0; }
.nm, .hosp { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hosp { font-size: 12px; font-weight: 400; color: #4a5568; }
.prk-row.me .hosp { color: #e3eefa; }
.prk-src-btn { align-self: flex-start; display: inline-flex; align-items: center; gap: 6px; background: none; border: none; padding: 8px 0; font: inherit; font-size: 14px; font-weight: 600; color: var(--brand-ocean-600); cursor: pointer; }
.prk-src { background: #fff; border: 1px solid #e6e1d4; border-radius: 8px; padding: 10px 12px; display: flex; flex-direction: column; gap: 6px; }
.cap { font-size: 12px; font-weight: 700; color: #4a5568; text-transform: uppercase; letter-spacing: 0.04em; }
.prk-src ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
.prk-src li { display: flex; align-items: center; gap: 6px; font-size: 13px; min-width: 0; }
.prk-src a { font-weight: 600; white-space: nowrap; color: var(--brand-ocean-600); }
.prk-src .plain { font-weight: 600; white-space: nowrap; }
.prk-src .t { color: #4a5568; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.prk-src .ok { color: #0E7C80; font-weight: 700; }
.prk-foot { font-size: 13px; color: #4a5568; }
@media (max-width: 600px) { .prk { padding: 16px; } .prk-grid { grid-template-columns: 1fr; } }
</style>
